// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// "WHERE IT APPLIES" — the site's only Leaflet map, and its centrepiece.
//
// Three things about this file are deliberate and load-bearing.
//
// 1. CANVAS CANNOT DRAW THE SVG TEXTURES. The rest of the site separates the
//    four outcome categories with a (hue, texture) pair. A canvas renderer has
//    no pattern fills, so the texture half is re-encoded here in channels that
//    DO survive canvas: fill opacity, outline weight and outline dash. Every one
//    of those is named in the legend card, so hue is never the only channel.
//    1,162 multipolygons with roughly 160k vertices are unusable under the SVG
//    renderer, which is why `preferCanvas` is not optional.
//
// 2. THE VIEW IS SET SYNCHRONOUSLY, at creation, over Australia. A map whose
//    view is only ever set inside `requestAnimationFrame` renders NOTHING — no
//    tiles, no polygons, no error — for anyone who opens the page in a
//    background tab, because rAF never fires there. And `fitBounds` on a 0x0
//    container silently returns MAXIMUM zoom and draws one street corner, so it
//    is guarded on a measured non-zero size. main.ts renders every view into a
//    DETACHED div and moves the children into the page afterwards, so this
//    container is genuinely 0x0 during render — the deferred layout pass below
//    is the only place the map can honestly be sized.
//
// 3. THE GEOMETRY IS PARSED ONCE. Toggling a category adds or removes a
//    pre-built layer group; toggling the year or sea country adds or removes
//    individual pre-built sub-layers. Nothing re-parses GeoJSON after mount.

import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import { TENURE, TENURE_ORDER, PENDING_STROKE, ALRA_CAVEAT, mixHex, type Tenure } from '../theme';
import { el, chart, measure, ticks } from '../components/svg';
import { esc, num, km2, pct, longDate, tip, tipAttr, plural } from '../format';
import { openDetermination, indexOutcomes } from '../components/detail';
import { glossTerm, glossIcon } from '../components/glossary';
import { currentRoute, navigate } from '../router';
import {
  getMeta, getDeterminations, getDetermination,
  loadSeries, loadJurisdictions, loadOutcomeGeometry, loadClaimGeometry,
  loadScheduleGeometry, loadRatsibGeometry, loadAgreementGeometry, loadStates, loadOutcomes,
} from '../data';
import type { OutcomePolygonRow, Determination, Jurisdiction } from '../types';

/* ── the outcomes layer's own properties, exactly as the Tribunal writes them ─ */

interface OutcomeProps {
  TRIBID: string;
  TEN: Tenure;
  NAME: string | null;
  DETDATE: string | null;
  DETMETHOD: string | null;
  JURIS: string;
  SEADET: string | null;
  JUDGE: string | null;
  RNTBCNAME: string | null;
}

interface StateProps { code: string; name: string }

/**
 * DRAW ORDER, fixed and stated in the legend.
 *
 * The two findings that do NOT recognise native title go down first, so an
 * overlapping recognition cannot silently paint over a "does not exist" finding
 * — or the reverse. It is not a z-order convenience; it is the order that makes
 * the map's claim about any given piece of ground reproducible.
 */
const DRAW_ORDER: Tenure[] = ['notexist', 'extinguished', 'nonexclusive', 'exclusive'];

/**
 * The canvas-safe encoding of each category.
 *
 * `channel` is not a comment — it is printed on the legend card, because a
 * reader cannot use a redundant channel that nobody told them about. Outline
 * colours are derived from the category's own fill (darkened toward the body
 * text colour) so no new hue enters the palette.
 */
const CAT: Record<Tenure, { fillOpacity: number; weight: number; dashArray?: string; channel: string }> = {
  exclusive: {
    fillOpacity: 0.82, weight: 0.7,
    channel: 'Solid deep ochre, filled strongly, thin unbroken outline.',
  },
  nonexclusive: {
    fillOpacity: 0.40, weight: 1.2,
    channel: 'The same ochre family but much paler, with a heavier unbroken outline.',
  },
  notexist: {
    fillOpacity: 0.72, weight: 0.9, dashArray: '1 2',
    channel: 'Solid slate grey, filled strongly, dotted outline.',
  },
  extinguished: {
    fillOpacity: 0.36, weight: 1.3, dashArray: '4 3',
    channel: 'Pale slate grey, lightly filled, long-dashed outline.',
  },
};

const INK = '#1a1815';
const strokeFor = (t: Tenure) => mixHex(TENURE[t].fill, INK, 0.45);

type StyleState = 'normal' | 'sel' | 'dim';

function styleFor(t: Tenure, state: StyleState): L.PathOptions {
  const c = CAT[t];
  const base: L.PathOptions = {
    color: strokeFor(t),
    weight: c.weight,
    dashArray: c.dashArray,
    opacity: 1,
    fillColor: TENURE[t].fill,
    fillOpacity: c.fillOpacity,
    fill: true,
  };
  if (state === 'sel') return { ...base, color: INK, weight: c.weight + 1.8, dashArray: undefined, fillOpacity: Math.min(0.92, c.fillOpacity + 0.16) };
  if (state === 'dim') return { ...base, opacity: 0.14, fillOpacity: c.fillOpacity * 0.16 };
  return base;
}

/* ── the optional overlays, each loaded only when its toggle is first used ──── */

interface OverlayDef {
  key: string;
  label: string;
  count: number;
  plain: string;
  lineStyle: 'solid' | 'dashed' | 'dotted';
  colour: string;
  style: L.PathOptions;
  load: () => Promise<GeoJSON.FeatureCollection>;
  /** Every overlay mark carries its own tooltip too. */
  tipHtml: (p: Record<string, unknown>) => string;
}

const RATSIB_INK = '#635d51';
const AGREEMENT_INK = '#47433b';

function s(v: unknown): string | null {
  return typeof v === 'string' && v.trim() ? v : null;
}

function isoDay(v: unknown): string | null {
  const raw = s(v);
  return raw ? raw.slice(0, 10) : null;
}

/* ── outcomes.json goes through the SHARED loader in src/data.ts, which keeps
   one in-flight promise per file. Fetching it directly here — as an earlier
   version did — meant a reader who opened both this view and the register
   downloaded the same file twice, and re-downloaded it on every return to this
   view, because a local `fetch` has no idea the other caller exists. */

/* ─────────────────────────────────────────────────────────────────────────── */

interface Entry {
  layer: L.Polygon;
  tenure: Tenure;
  tribid: string;
  year: number;
  sea: boolean;
  areaKm2: number | null;
  props: OutcomeProps;
  /** In its category group right now? */
  on: boolean;
  state: StyleState;
}

export async function renderMap(host: HTMLElement): Promise<void> {
  const meta = getMeta();
  const dets = getDeterminations();

  const [fc, rows, series, jurs, statesFc] = await Promise.all([
    loadOutcomeGeometry(),
    loadOutcomes(),
    loadSeries(),
    loadJurisdictions(),
    loadStates(),
  ]);

  // Hand the outcome rows to the shared determination panel, so opening a
  // determination from this map shows the same area-by-area breakdown it shows
  // everywhere else.
  indexOutcomes(rows);

  const rowsById = new Map<string, OutcomePolygonRow[]>();
  for (const r of rows) {
    const list = rowsById.get(r.id);
    if (list) list.push(r);
    else rowsById.set(r.id, [r]);
  }

  const feats = fc.features as GeoJSON.Feature<GeoJSON.Geometry, OutcomeProps>[];

  /**
   * PER-POLYGON AREA, or nothing at all.
   *
   * outcomes.json and outcomes.geojson are written by the same pipeline pass and
   * are index-aligned. That alignment is CHECKED here rather than assumed: if it
   * ever stops holding, every polygon reports "area not available in this layer"
   * and the reader is sent to the determination panel, which indexes by id and
   * is exact. Silently attaching a plausible wrong number would be worse than
   * attaching none.
   */
  const aligned = rows.length === feats.length
    && feats.every((f, i) => rows[i].id === f.properties.TRIBID && rows[i].tenure === f.properties.TEN);
  const areaQueue: Record<string, number[]> = { exclusive: [], nonexclusive: [], notexist: [], extinguished: [] };
  if (aligned) for (const r of rows) if (r.tenure) areaQueue[r.tenure].push(r.areaSqKm);
  const areaCursor: Record<string, number> = { exclusive: 0, nonexclusive: 0, notexist: 0, extinguished: 0 };

  const entries: Entry[] = [];
  const byTribid = new Map<string, Entry[]>();

  const YEAR_MIN = 1992;
  const cumulative = series.cumulativeRecognised;
  const YEAR_MAX = cumulative.length ? cumulative[cumulative.length - 1].year : new Date().getUTCFullYear();

  let cutYear = YEAR_MAX;
  let seaOn = false;
  let selected: string | null = null;
  let activeJuris: string | null = null;
  const catOn: Record<Tenure, boolean> = { exclusive: true, nonexclusive: true, notexist: true, extinguished: true };

  /* ─────────────────────────────── the page ───────────────────────────────── */

  const partsCount = dets.filter((d) => d.finding === 'parts').length;
  const noneCount = dets.filter((d) => d.finding === 'none').length;
  const withPolygons = new Set(feats.map((f) => f.properties.TRIBID)).size;

  const jurByCode = new Map(jurs.map((j) => [j.code, j]));

  host.innerHTML = `
    <div class="view-head">
      <h1>Where it applies</h1>
      <p class="lede">Australian courts have recognised native title over ${esc(km2(meta.areas.recognisedKm2))} of land —
        ${esc(pct(meta.areas.recognisedPct))} of the continent. This map shows where, and what kind. Click any area and
        every other part of the same ${glossTerm('determination')} lights up with it: ${esc(num(partsCount))} of the
        ${esc(num(meta.counts.determinations))} determinations on the register found that native title exists in
        <em>parts</em> of the area the court considered, so a determination is usually a mosaic of several categories
        rather than one block of colour.</p>
    </div>

    <div class="map-controls">
      <div class="map-search-field">
        <label class="field-label" for="map-search">Find a determination by name, by who holds it, or by its
          ${glossTerm('rntbc', 'body corporate')}</label>
        <input type="search" id="map-search" autocomplete="off" spellcheck="false"
          placeholder="e.g. Croker Island, Yindjibarndi, Kaurareg…">
        <ul class="map-results" id="map-results" aria-live="polite"></ul>
      </div>
      <div>
        <span class="field-label">Optional layers — each is fetched only when you first switch it on</span>
        <div class="map-toggles" id="map-toggles"></div>
      </div>
    </div>

    <div>
      <span class="field-label">Go to a jurisdiction. Every jurisdiction is listed, including the two with none.</span>
      <div class="map-jchips" id="map-jchips"></div>
    </div>

    <div class="map-frame" id="map-frame"></div>

    <div class="pinned-caveat">
      <strong>Indicative boundaries. Not legal advice.</strong>
      Every line on this map comes from the National Native Title Tribunal's spatial layers and is
      <strong>indicative only</strong>. These are not cadastral or legal boundaries, they cannot be used to establish
      rights over any particular parcel of land or water, and nothing here is legal advice. For the position that
      binds anyone, read the Federal Court's orders for that determination.
    </div>

    <div class="map-scrub card" id="map-scrub">
      <div class="row">
        <div class="grow">
          <label class="field-label" for="map-year">Show only determinations made up to the end of
            <strong id="map-year-label" class="mono">${esc(String(YEAR_MAX))}</strong></label>
          <input type="range" id="map-year" min="${YEAR_MIN}" max="${YEAR_MAX}" step="1" value="${YEAR_MAX}"
            aria-describedby="map-year-read">
        </div>
        <button type="button" class="btn btn-sm shrink0" id="map-year-reset">Back to today</button>
      </div>
      <div class="map-scrub-read" id="map-year-read"></div>
      <div class="chart-frame" id="map-spark"></div>
      <p class="xsmall muted">The area figure is read straight from the published cumulative series — it is a dissolved
        union measured once in an ${glossTerm('equal-area-projection')}, never a sum of the polygons currently on
        screen. It counts land only, so it does not move when sea country is switched on.</p>
    </div>

    <div class="map-sel card" id="map-sel"></div>

    <h2 class="card-title" style="margin-top:var(--space-xl)">What the four categories mean${glossIcon('determination-area')}</h2>
    <p class="small muted" id="map-legend-lead"></p>
    <div class="legend-cards" id="map-legend"></div>
    <div class="row" style="margin-top:var(--space-sm)">
      <button type="button" class="btn btn-sm" id="map-legend-all">Show all four again</button>
      <span class="xsmall muted">Drawn bottom to top in this order: does not exist, extinguished, non-exclusive,
        exclusive — so an overlapping category cannot hide another.</span>
    </div>

    <div class="note warn caveat" id="map-alra">
      <strong>Why the Northern Territory looks low.</strong>
      ${esc(ALRA_CAVEAT)}
      <div class="row" style="margin-top:var(--space-sm)">
        <button type="button" class="btn btn-sm" id="map-alra-more" aria-expanded="false" aria-controls="map-alra-body">
          Show the Territory's figures</button>
        ${glossTerm('alra', 'What is that Act?')}
      </div>
      <div class="map-expand-body" id="map-alra-body" hidden></div>
    </div>

    <div class="note caveat">
      <strong>Sea country is excluded from every percentage.</strong>
      Native title has also been recognised over ${esc(km2(meta.areas.recognisedSeaKm2))} of
      ${glossTerm('sea-determination', 'sea country')}, and it is almost always non-exclusive. Because the denominator
      on this site is Australia's land area, sea determinations are left out of every share and every area figure —
      so they are switched <strong>off</strong> by default here. Switch "Sea country" on above to see them drawn; the
      cumulative area readout will not change, because it has always been land only.
    </div>

    <div class="note caveat">
      <strong>What the map cannot show you.</strong>
      ${esc(num(meta.counts.determinations - withPolygons))} of the ${esc(num(meta.counts.determinations))}
      determinations carry no outcome polygons at all — ${glossTerm('compensation', 'compensation matters')} decide
      money rather than dividing ground into categories — so they exist on the register but not on this map. Every
      determination, including those, is searchable in the box above and listed in full under "The record".
      ${esc(num(noneCount))} determinations found that native title does not exist in the area considered; that is a
      finding about ground, and it is drawn at the same visual weight as every other finding.
      <div class="row" style="margin-top:var(--space-sm)">
        <button type="button" class="btn btn-sm" id="map-goto-juris">See how the jurisdictions compare</button>
      </div>
    </div>
  `;

  const frame = host.querySelector<HTMLDivElement>('#map-frame')!;
  const legendHost = host.querySelector<HTMLDivElement>('#map-legend')!;
  const legendLead = host.querySelector<HTMLElement>('#map-legend-lead')!;
  const selHost = host.querySelector<HTMLDivElement>('#map-sel')!;
  const chipHost = host.querySelector<HTMLDivElement>('#map-jchips')!;
  const toggleHost = host.querySelector<HTMLDivElement>('#map-toggles')!;
  const searchInput = host.querySelector<HTMLInputElement>('#map-search')!;
  const resultsHost = host.querySelector<HTMLUListElement>('#map-results')!;
  const yearInput = host.querySelector<HTMLInputElement>('#map-year')!;
  const yearLabel = host.querySelector<HTMLElement>('#map-year-label')!;
  const yearRead = host.querySelector<HTMLElement>('#map-year-read')!;
  const sparkHost = host.querySelector<HTMLDivElement>('#map-spark')!;

  legendLead.textContent = `Every category is a hue AND a way of drawing the outline, so all four separate in greyscale `
    + `and for a reader who cannot tell the two hues apart. Click a card to see that category on its own; click it `
    + `again to bring the other three back. ${plural(meta.counts.outcomePolygons, 'outcome polygon')} in total.`;

  /* ─────────────────────────────── the map ────────────────────────────────── */

  const map = L.map(frame, {
    // 1,162 multipolygons under the SVG renderer make this view unusable.
    preferCanvas: true,
    zoomControl: true,
    attributionControl: true,
    worldCopyJump: false,
    /**
     * ZOOM ANIMATION IS OFF, AND THAT IS LOAD-BEARING.
     *
     * Leaflet animates a zoom by putting a `scale()` on `.leaflet-tile-container`
     * and clearing it on `zoomend`. If a programmatic `fitBounds` lands while a
     * zoom animation is still in flight — which is exactly what happens when the
     * deferred layout pass below fires `invalidateSize()` and then fits, on a
     * container that has only just been given its size — the animation never
     * ends and that scale is never cleared.
     *
     * The result shipped once and is genuinely hard to recognise: the map sits
     * at `_zoom` 4, reports zoom 4, requests zoom-4 tiles, and draws every one
     * of them at 128px instead of 256px. So it renders exactly one zoom level
     * too far out — 219° of longitude across the viewport instead of 110° —
     * with Australia a small shape near the bottom edge and Africa on the left.
     * Nothing errors. `getZoom()` is truthful. Only the tile container's
     * transform gives it away.
     *
     * With `zoomAnimation: false` Leaflet never applies that scale at all, so
     * the failure is structurally impossible rather than merely unlikely. The
     * cost is a hard cut between zoom levels, which on a canvas layer of 1,162
     * multipolygons was never smooth anyway.
     */
    zoomAnimation: false,
    /**
     * Australia occupies about 110° of longitude. Below zoom 3 the viewport
     * holds most of a hemisphere and every polygon on this map is a speck, so
     * there is nothing to see and no reason to allow it.
     */
    minZoom: 3,
  });

  // SYNCHRONOUS, at creation. Everything below may be deferred; this may not.
  // A view set only inside requestAnimationFrame renders nothing at all — no
  // tiles, no layers, no error — for anyone who opens the page in a background
  // tab, because rAF never fires there.
  map.setView([-26.2, 134.0], 4);

  L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
    subdomains: 'abcd',
    maxZoom: 18,
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> contributors, &copy; <a href="https://carto.com/attributions" target="_blank" rel="noopener">CARTO</a>',
  }).addTo(map);

  map.attributionControl.addAttribution(
    'Boundaries: National Native Title Tribunal (<a href="https://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noopener">CC BY 4.0</a>) — indicative only',
  );

  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ── the jurisdiction reference layer, drawn beneath everything ──────────── */

  const jurisBounds = new Map<string, L.LatLngBounds>();

  const statesLayer = L.geoJSON<StateProps>(statesFc, {
    style: () => ({ color: '#8a8072', weight: 1.1, opacity: 0.9, fill: true, fillColor: '#8a8072', fillOpacity: 0, dashArray: '2 4' }),
    onEachFeature(feature, layer) {
      const p = feature.properties;
      const j = jurByCode.get(p.code);
      jurisBounds.set(p.code, (layer as L.Polygon).getBounds());
      const zero = !j || j.determinations === 0;
      const lines = [
        `<div class="map-tip-name">${esc(p.name)}</div>`,
        `<div class="map-tip-row">${zero
          ? '0 determinations, 0 live matters — a real zero on this register, not missing data.'
          : `${esc(plural(j!.determinations, 'determination'))} · ${esc(pct(j!.recognisedPct))} of its land area recognised`}</div>`,
        '<div class="map-tip-row">Click to zoom to this jurisdiction.</div>',
      ];
      layer.bindTooltip(lines.join(''), { sticky: true, direction: 'top', className: 'map-tip', opacity: 0.98 });
      layer.on('click', () => selectJuris(p.code));
    },
  });

  const auBounds = statesLayer.getBounds();

  /**
   * IS THIS CONTAINER ACTUALLY A MAP YET?
   *
   * `rect.width > 0` is NOT the guard it looks like. A `.map-frame` whose
   * content box has collapsed still measures two pixels wide — its own borders —
   * and that passes a "> 0" test while `map.getSize().x` is exactly 0. Fitting
   * against a zero-width viewport returns zoom 0, and because the initial fit
   * only ever runs once, the map is then welded to a view of the whole planet
   * with no error anywhere — which is exactly how this view first behaved.
   */
  const SIZED = 80;
  const mapIsSized = (): boolean => {
    const rect = frame.getBoundingClientRect();
    if (rect.width < SIZED || rect.height < SIZED) return false;
    const size = map.getSize();
    return size.x >= SIZED && size.y >= SIZED;
  };

  /* ── the outcome layers: one group per category, parsed exactly once ─────── */

  const groups = {} as Record<Tenure, L.GeoJSON<OutcomeProps>>;

  for (const t of TENURE_ORDER) {
    groups[t] = L.geoJSON<OutcomeProps>(fc, {
      filter: (f) => f.properties?.TEN === t,
      style: () => styleFor(t, 'normal'),
      onEachFeature(feature, layer) {
        const p = feature.properties;
        const poly = layer as L.Polygon;
        const area = aligned ? (areaQueue[t][areaCursor[t]++] ?? null) : null;
        const year = p.DETDATE ? Number(String(p.DETDATE).slice(0, 4)) : YEAR_MAX;
        const entry: Entry = {
          layer: poly,
          tenure: t,
          tribid: p.TRIBID,
          year: Number.isFinite(year) ? year : YEAR_MAX,
          sea: p.SEADET === 'Y',
          areaKm2: area,
          props: p,
          on: true,
          state: 'normal',
        };
        entries.push(entry);
        const list = byTribid.get(p.TRIBID);
        if (list) list.push(entry);
        else byTribid.set(p.TRIBID, [entry]);

        poly.bindTooltip(outcomeTip(entry), { sticky: true, direction: 'top', className: 'map-tip', opacity: 0.98 });
        poly.on('click', () => select(entry.tribid, { open: true, fly: false }));
      },
    });
  }

  function outcomeTip(e: Entry): string {
    const t = TENURE[e.tenure];
    const j = jurByCode.get(e.props.JURIS);
    return [
      `<div class="map-tip-name verbatim">${esc(e.props.NAME ?? e.tribid)}</div>`,
      `<div class="map-tip-row">${esc(j?.name ?? e.props.JURIS)} · determined ${esc(longDate(isoDay(e.props.DETDATE)))}`
        + `${e.props.DETMETHOD ? ` · ${esc(e.props.DETMETHOD.toLowerCase())}` : ''}</div>`,
      `<div class="map-tip-row"><strong>${esc(t.register)}</strong></div>`,
      `<div class="map-tip-row">This part: ${e.areaKm2 == null
        ? 'area not available in this layer'
        : esc(km2(e.areaKm2, { exact: true }))}</div>`,
      e.sea ? '<div class="map-tip-row">Sea determination — excluded from every percentage on this site.</div>' : '',
      '<div class="map-tip-row">Click to light up every part of this determination and open the full record.</div>',
    ].filter(Boolean).join('');
  }

  /* ── optional overlays ──────────────────────────────────────────────────── */

  const OVERLAYS: OverlayDef[] = [
    {
      key: 'claims',
      label: 'Registered claims',
      count: meta.counts.registeredClaims,
      plain: 'Applications that have passed the registration test and are on the Register of Native Title Claims. Nothing has been decided about them — so they are drawn as an unfilled outline, never as a finding.',
      lineStyle: 'solid',
      colour: PENDING_STROKE,
      style: { color: PENDING_STROKE, weight: 1.5, opacity: 0.95, fill: false, dashArray: undefined },
      load: loadClaimGeometry,
      tipHtml: (p) => [
        `<div class="map-tip-name verbatim">${esc(s(p.NAME) ?? s(p.TRIBID) ?? 'Registered claim')}</div>`,
        '<div class="map-tip-row"><strong>Registered claim — nothing decided yet.</strong></div>',
        `<div class="map-tip-row">${esc(s(p.APPLTYPE) ?? 'Application')} · lodged ${esc(longDate(isoDay(p.DATELODGED)))} · ${esc(s(p.JURIS) ?? '—')}</div>`,
        `<div class="map-tip-row">Registration status: ${esc(s(p.RTSTATUS) ?? 'Not recorded')}</div>`,
        `<div class="map-tip-row">Representative body: ${esc(s(p.REP) ?? 'Not recorded')}</div>`,
      ].join(''),
    },
    {
      key: 'schedule',
      label: 'Live applications',
      count: meta.counts.scheduledApplications,
      plain: 'Every application filed with the Federal Court that is still live, whether or not it has passed the registration test. Unfilled, and dashed to separate it from the registered claims.',
      lineStyle: 'dashed',
      colour: PENDING_STROKE,
      style: { color: PENDING_STROKE, weight: 1.4, opacity: 0.95, fill: false, dashArray: '5 4' },
      load: loadScheduleGeometry,
      tipHtml: (p) => [
        `<div class="map-tip-name verbatim">${esc(s(p.NAME) ?? s(p.TRIBID) ?? 'Application')}</div>`,
        '<div class="map-tip-row"><strong>Live application — nothing decided yet.</strong></div>',
        `<div class="map-tip-row">${esc(s(p.APPLTYPE) ?? 'Application')} · lodged ${esc(longDate(isoDay(p.DATELODGED)))} · ${esc(s(p.JURIS) ?? '—')}</div>`,
        `<div class="map-tip-row">Registration status: ${esc(s(p.RTSTATUS) ?? 'Not recorded')}</div>`,
        `<div class="map-tip-row">Representative body: ${esc(s(p.REP) ?? 'Not recorded')}</div>`,
      ].join(''),
    },
    {
      key: 'ratsib',
      label: 'Representative bodies',
      count: meta.counts.representativeRegions,
      plain: 'The 18 regions in which a recognised representative body or service provider assists claimants and holders. An administrative boundary, drawn in neutral ink so it can never read as an outcome.',
      lineStyle: 'solid',
      colour: RATSIB_INK,
      style: { color: RATSIB_INK, weight: 2.2, opacity: 0.85, fill: false },
      load: loadRatsibGeometry,
      tipHtml: (p) => [
        `<div class="map-tip-name verbatim">${esc(s(p.ORG) ?? 'Representative body')}</div>`,
        `<div class="map-tip-row">Region: ${esc(s(p.NAME) ?? 'Not recorded')} · ${esc(s(p.JURIS) ?? '—')}</div>`,
        `<div class="map-tip-row">${esc(s(p.RATSIBTYPE) ?? 'Not recorded')}</div>`,
      ].join(''),
    },
    {
      key: 'agreements',
      label: 'Agreements (ILUAs)',
      count: meta.counts.agreements,
      plain: 'Registered Indigenous Land Use Agreements, or agreements currently in notification. They overlap each other heavily, so they are drawn as a fine dotted outline with no fill at all.',
      lineStyle: 'dotted',
      colour: AGREEMENT_INK,
      style: { color: AGREEMENT_INK, weight: 0.9, opacity: 0.7, fill: false, dashArray: '1 3' },
      load: loadAgreementGeometry,
      tipHtml: (p) => [
        `<div class="map-tip-name verbatim">${esc(s(p.NAME) ?? s(p.TRIBID) ?? 'Agreement')}</div>`,
        `<div class="map-tip-row">${esc(s(p.AGTYPE) ?? 'Agreement')} · ${esc(s(p.JURIS) ?? '—')}</div>`,
        `<div class="map-tip-row">Registered: ${esc(isoDay(p.DATEREGD) ? longDate(isoDay(p.DATEREGD)) : 'Not recorded — in notification')}</div>`,
        `<div class="map-tip-row">Subject matter: ${esc(s(p.SUBJMATTER) ?? 'Not recorded')}</div>`,
      ].join(''),
    },
  ];

  const overlayLayers = new Map<string, L.GeoJSON>();
  const overlayOn = new Set<string>();

  /* ── stacking ───────────────────────────────────────────────────────────── */

  /**
   * Rebuild the stack in the fixed order. Nothing here re-parses GeoJSON: the
   * groups already exist, so this is an add/remove of layers Leaflet has held
   * since mount.
   */
  function restack(): void {
    if (map.hasLayer(statesLayer)) map.removeLayer(statesLayer);
    for (const t of TENURE_ORDER) if (map.hasLayer(groups[t])) map.removeLayer(groups[t]);
    for (const layer of overlayLayers.values()) if (map.hasLayer(layer)) map.removeLayer(layer);

    statesLayer.addTo(map);
    for (const t of DRAW_ORDER) if (catOn[t]) groups[t].addTo(map);
    for (const def of OVERLAYS) {
      const layer = overlayLayers.get(def.key);
      if (layer && overlayOn.has(def.key)) layer.addTo(map);
    }
  }

  /* ── filtering by year and by sea country ───────────────────────────────── */

  function wanted(e: Entry): boolean {
    if (!seaOn && e.sea) return false;
    return e.year <= cutYear;
  }

  function applyFeatureFilter(): void {
    for (const e of entries) {
      const next = wanted(e);
      if (next === e.on) continue;
      e.on = next;
      if (next) groups[e.tenure].addLayer(e.layer);
      else groups[e.tenure].removeLayer(e.layer);
    }
  }

  /* ── selection: every sibling polygon of the same determination ─────────── */

  function applySelectionStyles(): void {
    for (const e of entries) {
      const next: StyleState = selected == null ? 'normal' : (e.tribid === selected ? 'sel' : 'dim');
      if (next === e.state) continue;
      e.state = next;
      e.layer.setStyle(styleFor(e.tenure, next));
    }
  }

  function select(id: string | null, opts: { open?: boolean; fly?: boolean } = {}): void {
    selected = id;
    applySelectionStyles();
    renderSelection();
    if (id && opts.fly) {
      const list = byTribid.get(id);
      if (list?.length) {
        let b = list[0].layer.getBounds();
        for (const e of list.slice(1)) b = b.extend(e.layer.getBounds());
        if (b.isValid() && mapIsSized()) {
          map.fitBounds(b.pad(0.25), { animate: !reduceMotion });
        }
      }
    }
    if (id && opts.open) {
      const d = getDetermination(id);
      if (d) openDetermination(d);
    }
  }

  /* ── the selection strip ────────────────────────────────────────────────── */

  function renderSelection(): void {
    if (!selected) {
      selHost.innerHTML = `
        <div class="card-title">Nothing selected yet</div>
        <p class="small muted" style="margin-top:var(--space-xs)">Click anywhere on the map, or search above.
          Every other part of the same determination will light up at the same time — which is the point of this view.
          ${esc(num(partsCount))} of ${esc(num(meta.counts.determinations))} determinations found native title exists in
          <em>parts</em> of the area considered, so most of them are a mosaic of two, three or four of the categories
          below.</p>`;
      return;
    }

    const d = getDetermination(selected);
    const parts = byTribid.get(selected) ?? [];
    const detRows = rowsById.get(selected) ?? [];
    const counts = new Map<Tenure, number>();
    for (const r of detRows) if (r.tenure) counts.set(r.tenure, (counts.get(r.tenure) ?? 0) + 1);

    const first = parts[0]?.props;
    const name = d?.name ?? first?.NAME ?? selected;
    const jurisCode = d?.juris ?? first?.JURIS ?? null;
    const seaParts = detRows.filter((r) => r.sea).length;

    // How many of this determination's parts are actually on screen right now,
    // and — if some are not — WHY. A reader who searches for a determination and
    // is flown to an empty patch of map has been told nothing; naming the filter
    // that is hiding it, and offering to lift it, is the difference.
    const drawn = parts.filter((e) => e.on && catOn[e.tenure]).length;
    const hiddenBySea = !seaOn && parts.some((e) => e.sea);
    const hiddenByYear = parts.some((e) => e.year > cutYear);
    const hiddenByCategory = parts.some((e) => !catOn[e.tenure]);
    const reasons = [
      hiddenBySea && 'sea country is switched off',
      hiddenByYear && `the year scrubber stops at ${cutYear}`,
      hiddenByCategory && 'one or more categories are hidden',
    ].filter(Boolean) as string[];

    const chips = TENURE_ORDER.filter((t) => counts.has(t)).map((t) => {
      const n = counts.get(t)!;
      return `<span class="pill" data-tip="${tipAttr(TENURE[t].register, TENURE[t].plain)}">
        <span class="legend-swatch" style="background:${esc(TENURE[t].fill)}"></span>
        ${esc(TENURE[t].label)} — ${esc(plural(n, 'part'))}</span>`;
    }).join('');

    selHost.innerHTML = `
      <div class="row">
        <div class="grow">
          <div class="card-sub">Selected determination</div>
          <div class="verbatim card-title">${esc(name)}</div>
        </div>
        <button type="button" class="btn btn-sm shrink0" id="map-sel-open">Open the full record</button>
        <button type="button" class="btn btn-sm shrink0" id="map-sel-clear">Clear the selection</button>
      </div>
      <div class="detail-headline" style="margin-top:var(--space-sm)">
        ${d?.date ? `<span class="pill">Determined ${esc(longDate(d.date))}</span>` : ''}
        ${d?.method ? `<span class="pill">${esc(d.method === 'consent' ? 'By consent' : d.method === 'unopposed' ? 'Unopposed' : 'Litigated')}</span>` : ''}
        ${jurisCode ? `<span class="pill">${esc(jurByCode.get(jurisCode)?.name ?? jurisCode)}</span>` : ''}
        ${d?.judge ? `<span class="pill">${esc(d.judge)}</span>` : ''}
        ${seaParts ? `<span class="pill">${esc(plural(seaParts, 'sea part'))}</span>` : ''}
      </div>
      <p class="small" style="margin-top:var(--space-sm)">The register divides this determination into
        ${esc(plural(parts.length, 'part'))}${parts.length && drawn !== parts.length
          ? `, of which ${esc(num(drawn))} ${drawn === 1 ? 'is' : 'are'} on the map right now`
          : ''}.
        ${d?.areaSqKm != null ? `The court considered ${esc(km2(d.areaSqKm, { exact: true }))} in total.` : ''}
        Open the full record for the area held in each category, the description of who holds native title, and the
        judgment.</p>
      ${parts.length && drawn < parts.length ? `<div class="note small">
        ${drawn === 0 ? 'None of this determination’s parts are drawn at the moment' : 'Some of its parts are hidden at the moment'}
        because ${esc(reasons.join(', and '))}.
        <button type="button" class="btn btn-sm" id="map-sel-reveal" style="margin-top:var(--space-sm)">Lift those filters and show it</button>
      </div>` : ''}
      <div class="map-sel-parts" style="margin-top:var(--space-sm)">${chips || '<span class="pill">No outcome polygons — this determination decided compensation rather than dividing ground into categories.</span>'}</div>
    `;

    selHost.querySelector<HTMLButtonElement>('#map-sel-open')?.addEventListener('click', () => {
      const det = selected ? getDetermination(selected) : undefined;
      if (det) openDetermination(det);
    });
    selHost.querySelector<HTMLButtonElement>('#map-sel-clear')?.addEventListener('click', () => select(null));
    selHost.querySelector<HTMLButtonElement>('#map-sel-reveal')?.addEventListener('click', () => {
      if (hiddenBySea) { seaOn = true; renderToggles(); }
      if (hiddenByYear) setYear(Math.max(...parts.map((e) => e.year)));
      if (hiddenByCategory) { for (const x of TENURE_ORDER) catOn[x] = true; restack(); renderLegend(); }
      applyFeatureFilter();
      renderYearReadout();
      renderSelection();
      if (selected) select(selected, { fly: true });
    });
  }

  /* ── the four teaching cards ────────────────────────────────────────────── */

  const CARD_AREA: Record<Tenure, number> = {
    exclusive: meta.areas.exclusiveKm2,
    nonexclusive: meta.areas.nonExclusiveKm2,
    notexist: meta.areas.notExistKm2,
    extinguished: meta.areas.extinguishedKm2,
  };

  function renderLegend(): void {
    legendHost.innerHTML = '';
    for (const t of TENURE_ORDER) {
      const style = TENURE[t];
      const area = CARD_AREA[t];
      const share = (area / meta.australiaLandKm2) * 100;
      const on = catOn[t];
      const isolated = TENURE_ORDER.every((x) => (x === t ? catOn[x] : !catOn[x]));

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'legend-card';
      btn.setAttribute('aria-pressed', String(on));
      btn.setAttribute('aria-label', isolated
        ? `${style.label} is shown on its own. Activate to bring all four categories back.`
        : `Show only ${style.label}`);
      btn.innerHTML = `
        <span class="legend-swatch" style="background:${esc(style.fill)};opacity:${t === 'nonexclusive' || t === 'extinguished' ? '.62' : '1'};border-style:${CAT[t].dashArray ? 'dashed' : 'solid'}"></span>
        <span class="lc-body">
          <span class="lc-name">${esc(style.label)} <span class="map-lc-check">${on ? '✓ shown' : '✕ hidden'}</span></span>
          <span class="lc-num">${esc(km2(area))} · ${esc(pct(share))} of Australia's land</span>
          <span class="lc-plain">${esc(style.plain)}</span>
          <span class="map-lc-channel">How it is drawn: ${esc(CAT[t].channel)}</span>
        </span>`;
      btn.setAttribute('data-tip', tip(
        style.register,
        `${km2(area, { exact: true })} — a dissolved union, measured once in an equal-area projection.`,
        `${pct(share)} of Australia's ${num(meta.australiaLandKm2)} km² land area.`,
        isolated ? 'Activate to bring all four categories back.' : 'Activate to see this category on its own.',
      ));
      btn.addEventListener('click', () => {
        const nowIsolated = TENURE_ORDER.every((x) => (x === t ? catOn[x] : !catOn[x]));
        for (const x of TENURE_ORDER) catOn[x] = nowIsolated ? true : x === t;
        restack();
        renderLegend();
      });
      legendHost.append(btn);
    }
  }

  host.querySelector<HTMLButtonElement>('#map-legend-all')!.addEventListener('click', () => {
    for (const x of TENURE_ORDER) catOn[x] = true;
    restack();
    renderLegend();
  });

  /* ── jurisdiction chips, including the two real zeros ───────────────────── */

  function selectJuris(code: string | null): void {
    activeJuris = code;
    renderChips();
    const b = code ? jurisBounds.get(code) : auBounds;
    if (b && b.isValid() && mapIsSized()) {
      map.fitBounds(b.pad(0.05), { animate: !reduceMotion });
    }
  }

  function zeroNote(j: Jurisdiction): string {
    return `${j.name}: 0 determinations, 0 live matters, 0 agreements. That is a real zero on this register — `
      + 'the Tribunal publishes no determination and no live application for it — not a gap in the data. There is '
      + 'also no recognised representative body there.';
  }

  function renderChips(): void {
    chipHost.innerHTML = '';

    const all = document.createElement('button');
    all.type = 'button';
    all.className = 'map-jchip';
    all.setAttribute('aria-pressed', String(activeJuris == null));
    all.innerHTML = `All of Australia <span class="map-jchip-n">${esc(num(meta.counts.determinations))}</span>`;
    all.setAttribute('data-tip', tip(
      `All ${num(meta.counts.determinations)} determinations on the National Native Title Register.`,
      `Recognised: ${km2(meta.areas.recognisedKm2, { exact: true })} — ${pct(meta.areas.recognisedPct)} of Australia's land.`,
    ));
    all.addEventListener('click', () => selectJuris(null));
    chipHost.append(all);

    for (const j of [...jurs].sort((a, b) => b.determinations - a.determinations || a.name.localeCompare(b.name))) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = `map-jchip${j.determinations === 0 ? ' map-jchip-zero' : ''}`;
      btn.setAttribute('aria-pressed', String(activeJuris === j.code));
      btn.innerHTML = `${esc(j.name)} <span class="map-jchip-n">${esc(num(j.determinations))}</span>`;
      btn.setAttribute('data-tip', j.determinations === 0 ? tip(zeroNote(j), 'Activate to zoom there anyway.') : tip(
        `${j.name} — ${plural(j.determinations, 'determination')}`,
        `${km2(j.recognisedKm2, { exact: true })} recognised — ${pct(j.recognisedPct)} of its land area.`,
        `${plural(j.liveMatters, 'live matter')} · ${plural(j.agreements, 'registered agreement')}`,
        'Activate to zoom to this jurisdiction.',
      ));
      btn.addEventListener('click', () => {
        selectJuris(j.code);
        if (j.determinations === 0) {
          selHost.innerHTML = `
            <div class="card-title">${esc(j.name)} — a labelled zero</div>
            <p class="small" style="margin-top:var(--space-xs)">${esc(zeroNote(j))}</p>
            <p class="small muted">A zero here is not the same as "no data". It means the register has been checked and
              contains nothing for this jurisdiction, so nothing is drawn on the map above.</p>`;
          selected = null;
          applySelectionStyles();
        }
      });
      chipHost.append(btn);
    }
  }

  /* ── the optional-layer toggles ─────────────────────────────────────────── */

  function renderToggles(): void {
    toggleHost.innerHTML = '';

    const sea = document.createElement('button');
    sea.type = 'button';
    sea.className = 'map-toggle';
    sea.setAttribute('aria-pressed', String(seaOn));
    sea.innerHTML = `<span class="map-toggle-key" style="border-top-style:solid;border-top-color:${esc(TENURE.nonexclusive.fill)}"></span>Sea country`;
    sea.setAttribute('data-tip', tip(
      'Sea determinations (SEADET = Y in the register).',
      `${km2(meta.areas.recognisedSeaKm2, { exact: true })} of sea country has been recognised, almost all of it non-exclusive.`,
      'Off by default: every percentage on this site uses Australia’s LAND area as its denominator, so sea country is excluded from all of them.',
    ));
    sea.addEventListener('click', () => {
      seaOn = !seaOn;
      sea.setAttribute('aria-pressed', String(seaOn));
      applyFeatureFilter();
      renderYearReadout();
    });
    toggleHost.append(sea);

    for (const def of OVERLAYS) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'map-toggle';
      btn.setAttribute('aria-pressed', String(overlayOn.has(def.key)));
      btn.innerHTML = `<span class="map-toggle-key" style="border-top-style:${def.lineStyle};border-top-color:${esc(def.colour)}"></span>`
        + `${esc(def.label)} <span class="mono xsmall">${esc(num(def.count))}</span>`;
      btn.setAttribute('data-tip', tip(`${def.label} — ${num(def.count)} on the register.`, def.plain));
      btn.addEventListener('click', () => { void toggleOverlay(def, btn); });
      toggleHost.append(btn);
    }
  }

  async function toggleOverlay(def: OverlayDef, btn: HTMLButtonElement): Promise<void> {
    if (overlayOn.has(def.key)) {
      overlayOn.delete(def.key);
      btn.setAttribute('aria-pressed', 'false');
      restack();
      return;
    }

    if (!overlayLayers.has(def.key)) {
      btn.setAttribute('aria-busy', 'true');
      btn.disabled = true;
      try {
        const data = await def.load();
        const layer = L.geoJSON(data, {
          style: () => def.style,
          onEachFeature(feature, sub) {
            const p = (feature.properties ?? {}) as Record<string, unknown>;
            sub.bindTooltip(def.tipHtml(p), { sticky: true, direction: 'top', className: 'map-tip', opacity: 0.98 });
          },
        });
        overlayLayers.set(def.key, layer);
      } catch (err) {
        btn.removeAttribute('aria-busy');
        btn.disabled = false;
        btn.setAttribute('data-tip', tip(
          `${def.label} could not be loaded.`,
          err instanceof Error ? err.message : String(err),
          'Activate to try again.',
        ));
        return;
      }
      btn.removeAttribute('aria-busy');
      btn.disabled = false;
    }

    overlayOn.add(def.key);
    btn.setAttribute('aria-pressed', 'true');
    restack();
  }

  /* ── search ─────────────────────────────────────────────────────────────── */

  const searchIndex = dets.map((d) => ({
    d,
    hay: [d.name, d.holders, d.bodyCorporate, d.caseName, d.id].filter(Boolean).join('  ').toLowerCase(),
  }));

  const findingLabel = (d: Determination): string => {
    if (d.finding === 'entire') return 'Native title exists in the entire area';
    if (d.finding === 'parts') return 'Native title exists in parts of the area';
    if (d.finding === 'none') return 'Native title does not exist in the area considered';
    if (d.finding === 'compensation') return 'Compensation is payable';
    return d.findingText ?? 'Outcome not recorded';
  };

  function renderResults(q: string): void {
    const query = q.trim().toLowerCase();
    resultsHost.innerHTML = '';
    if (query.length < 2) return;

    const hits = searchIndex.filter((x) => x.hay.includes(query));
    if (!hits.length) {
      const li = document.createElement('li');
      li.className = 'map-results-more';
      li.textContent = 'No determination on the register matches that. Names, holders and body corporate names are all searched.';
      resultsHost.append(li);
      return;
    }

    for (const hit of hits.slice(0, 30)) {
      const d = hit.d;
      const hasGeometry = byTribid.has(d.id);
      const li = document.createElement('li');
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'map-result';
      btn.innerHTML = `<span class="verbatim">${esc(d.name ?? d.id)}</span>
        <span class="map-result-meta">${esc(jurByCode.get(d.juris)?.name ?? d.juris)} · ${esc(longDate(d.date))} ·
        ${esc(findingLabel(d))}${hasGeometry ? '' : ' · no outcome polygons'}</span>`;
      btn.addEventListener('click', () => {
        if (hasGeometry) {
          select(d.id, { fly: true, open: true });
        } else {
          select(null);
          openDetermination(d);
        }
        searchInput.value = d.name ?? d.id;
        resultsHost.innerHTML = '';
      });
      li.append(btn);
      resultsHost.append(li);
    }

    if (hits.length > 30) {
      const li = document.createElement('li');
      li.className = 'map-results-more';
      li.textContent = `${hits.length - 30} more match. Type a little more to narrow it.`;
      resultsHost.append(li);
    }
  }

  searchInput.addEventListener('input', () => renderResults(searchInput.value));

  /* ── the year scrubber and its cumulative readout ───────────────────────── */

  const cumAt = (year: number): number => {
    let v = 0;
    for (const p of cumulative) if (p.year <= year) v = p.areaKm2;
    return v;
  };

  function renderYearReadout(): void {
    const area = cumAt(cutYear);
    const visible = new Set<string>();
    for (const e of entries) if (e.on && catOn[e.tenure]) visible.add(e.tribid);
    yearLabel.textContent = String(cutYear);
    yearRead.innerHTML = `
      <span><span class="map-scrub-value">${esc(km2(area))}</span>
        <span class="small muted">recognised by the end of ${esc(String(cutYear))}</span></span>
      <span class="small muted">${esc(pct((area / meta.australiaLandKm2) * 100))} of Australia's land area</span>
      <span class="small muted">${esc(plural(visible.size, 'determination'))} drawn on the map right now</span>`;
    yearRead.setAttribute('data-tip', tip(
      `Cumulative recognised area to the end of ${cutYear}: ${km2(area, { exact: true })}.`,
      'Published as a dissolved union in an equal-area projection — never summed in your browser from the shapes on screen.',
      'Land only. Sea determinations are excluded from this figure whether or not they are drawn.',
    ));
  }

  function drawSpark(): void {
    sparkHost.innerHTML = '';
    if (!cumulative.length) return;

    const w = Math.max(260, measure(sparkHost, 640));
    const h = 140;
    const pad = { l: 58, r: 12, t: 12, b: 26 };
    const iw = Math.max(40, w - pad.l - pad.r);
    const ih = h - pad.t - pad.b;
    const max = cumulative[cumulative.length - 1].areaKm2 || 1;
    const n = cumulative.length;
    const x = (i: number) => pad.l + (n === 1 ? iw / 2 : (i / (n - 1)) * iw);
    const y = (v: number) => pad.t + ih - (v / max) * ih;

    const svg = chart(w, h, 'Cumulative recognised area, 1992 to the present, with the current year scrubber position');

    for (const t of ticks(max, 3)) {
      svg.append(el('line', { class: 'grid-line', x1: pad.l, x2: pad.l + iw, y1: y(t), y2: y(t) }));
      svg.append(el('text', {
        class: 'axis-text', x: pad.l - 6, y: y(t) + 3.5, 'text-anchor': 'end',
      }, t === 0 ? '0' : `${(t / 1_000_000).toFixed(1)}M km²`));
    }

    const areaPts = cumulative.map((p, i) => `${x(i).toFixed(1)},${y(p.areaKm2).toFixed(1)}`).join(' ');
    svg.append(el('polygon', {
      points: `${pad.l},${pad.t + ih} ${areaPts} ${pad.l + iw},${pad.t + ih}`,
      fill: mixHex(TENURE.exclusive.fill, TENURE.nonexclusive.fill, 0.5),
      'fill-opacity': 0.45,
      stroke: 'none',
    }));
    svg.append(el('polyline', {
      points: areaPts, fill: 'none',
      stroke: mixHex(TENURE.exclusive.fill, INK, 0.3), 'stroke-width': 1.6,
    }));

    const idx = Math.max(0, cumulative.findIndex((p) => p.year === cutYear));
    svg.append(el('line', {
      class: 'baseline', x1: x(idx), x2: x(idx), y1: pad.t, y2: pad.t + ih,
    }));
    svg.append(el('circle', {
      cx: x(idx), cy: y(cumulative[idx].areaKm2), r: 4, fill: INK,
    }));

    svg.append(el('line', { class: 'axis-line', x1: pad.l, x2: pad.l + iw, y1: pad.t + ih, y2: pad.t + ih }));

    const step = n > 1 ? iw / (n - 1) : iw;
    for (let i = 0; i < n; i++) {
      const p = cumulative[i];
      const prev = i > 0 ? cumulative[i - 1].areaKm2 : 0;
      const hit = el('rect', {
        class: 'hit',
        x: x(i) - step / 2, y: pad.t, width: step, height: ih,
        fill: '#ffffff', 'fill-opacity': 0, 'pointer-events': 'all',
      });
      hit.setAttribute('data-tip', tip(
        `To the end of ${p.year}`,
        `Recognised: ${km2(p.areaKm2, { exact: true })} — ${pct((p.areaKm2 / meta.australiaLandKm2) * 100, 2)} of Australia's land.`,
        i > 0 ? `Added during ${p.year}: ${km2(p.areaKm2 - prev, { exact: true })}` : 'The first determination was made on 3 June 1992.',
        'Click to move the year scrubber here.',
      ));
      hit.addEventListener('click', () => setYear(p.year));
      svg.append(hit);

      if (p.year % 5 === 0 || i === 0 || i === n - 1) {
        svg.append(el('text', {
          class: 'axis-text', x: x(i), y: h - 8, 'text-anchor': i === n - 1 ? 'end' : i === 0 ? 'start' : 'middle',
        }, String(p.year)));
      }
    }

    sparkHost.append(svg);
  }

  function setYear(year: number): void {
    const next = Math.min(YEAR_MAX, Math.max(YEAR_MIN, Math.round(year)));
    if (next === cutYear) return;
    cutYear = next;
    yearInput.value = String(next);
    applyFeatureFilter();
    renderYearReadout();
    drawSpark();
  }

  yearInput.addEventListener('input', () => setYear(Number(yearInput.value)));
  host.querySelector<HTMLButtonElement>('#map-year-reset')!.addEventListener('click', () => setYear(YEAR_MAX));

  /* ── the ALRA note ──────────────────────────────────────────────────────── */

  const alraBtn = host.querySelector<HTMLButtonElement>('#map-alra-more')!;
  const alraBody = host.querySelector<HTMLDivElement>('#map-alra-body')!;
  const nt = jurByCode.get('NT');
  alraBody.innerHTML = `<p class="small" style="margin-top:var(--space-sm)">On this register the Northern Territory
    shows ${esc(km2(nt?.recognisedKm2 ?? 0))} recognised — ${esc(pct(nt?.recognisedPct ?? 0))} of its
    ${esc(num(nt?.landKm2 ?? 0))} km² land area, from ${esc(plural(nt?.determinations ?? 0, 'determination'))}. Roughly
    half the Territory is separately held as inalienable Aboriginal freehold under the Aboriginal Land Rights
    (Northern Territory) Act 1976, which none of these seven registers records. Reading the Territory's figure as a
    measure of Aboriginal landholding is therefore a serious error, and it is the single most common one made with
    this map.</p>`;
  alraBtn.addEventListener('click', () => {
    const open = alraBtn.getAttribute('aria-expanded') === 'true';
    alraBtn.setAttribute('aria-expanded', String(!open));
    alraBody.hidden = open;
    alraBtn.textContent = open ? "Show the Territory's figures" : "Hide the Territory's figures";
  });

  host.querySelector<HTMLButtonElement>('#map-goto-juris')!.addEventListener('click', () => {
    navigate('jurisdictions', activeJuris ? { juris: activeJuris } : undefined);
  });

  /* ── first paint ────────────────────────────────────────────────────────── */

  // Sea country defaults to OFF, so the very first paint has to run the feature
  // filter too — without this the 108 sea polygons are drawn on load and only
  // disappear once someone touches the toggle, which is the opposite of what the
  // toggle says.
  applyFeatureFilter();
  restack();
  renderLegend();
  renderChips();
  renderToggles();
  renderSelection();
  renderYearReadout();
  drawSpark();

  // A deep link straight to one determination — the same ?id= the shared panel
  // writes — selects it here too, so a shared URL lands on the right country.
  const deepId = currentRoute().params.get('id');
  if (deepId && byTribid.has(deepId)) select(deepId, { fly: true });

  /**
   * THE DEFERRED LAYOUT PASS.
   *
   * main.ts builds each view inside a DETACHED div and only moves the children
   * into the page after `render` resolves, so at this point the container is
   * genuinely 0x0. `setTimeout(0)` runs after that move; the ResizeObserver and
   * the resize listener catch every later change.
   *
   * The initial fit happens ONCE, and only once `mapIsSized()` agrees the
   * container is a real viewport — see the note on that helper. Until then the
   * map keeps the synchronous Australia-wide view it was created with, which is
   * already a correct view, so a reader in a background tab is never shown a
   * blank or a planet-wide map.
   */
  /**
   * THE FIT IS VERIFIED BY ITS RESULT, NOT ASSUMED FROM ITS CALL.
   *
   * A one-shot `fitted = true` flag is the obvious way to write this and it is
   * why the view first shipped showing Australia adrift in a third of the
   * planet. The flag records that `fitBounds` was CALLED, which is not the same
   * as the map having ended up fitted: called against a container Leaflet still
   * believes is a hundred pixels wide, `fitBounds` returns a wildly wrong zoom,
   * sets the flag, and the map is then welded to that view forever. Every
   * subsequent tick sees `fitted` and does nothing.
   *
   * So the flag is only set once the OUTCOME is right — the viewport is holding
   * something close to Australia rather than half a hemisphere. Until then
   * every layout tick tries again, which costs nothing because the ticks stop
   * as soon as it works.
   *
   * `userMoved` exists because a reader is entitled to zoom out. Once they have
   * touched the map, re-fitting under them would be a worse bug than the one
   * this guards against, so the automatic fitting stops permanently.
   */
  const auSpan = auBounds.getEast() - auBounds.getWest();
  let fitted = false;
  let userMoved = false;
  // Only a real gesture counts. Leaflet fires its own move and zoom events for
  // programmatic calls too, so listening to those would have the map declare
  // itself "moved by the user" during its own initial fit.
  for (const ev of ['pointerdown', 'wheel'] as const) {
    frame.addEventListener(ev, () => { userMoved = true; }, { passive: true });
  }

  function layoutTick(): void {
    drawSpark();
    const rect = frame.getBoundingClientRect();
    if (rect.width < SIZED || rect.height < SIZED) return;
    map.invalidateSize({ animate: false });
    if (fitted || userMoved || !mapIsSized() || !auBounds.isValid()) return;

    map.fitBounds(auBounds.pad(0.02), { animate: false });

    /**
     * Success is "the whole country is on screen at a sane scale", not "the
     * country fills the frame".
     *
     * Leaflet's default `zoomSnap` rounds a fit DOWN to a whole zoom level, and
     * this frame is about twice as wide as Australia's own aspect ratio, so a
     * correct fit legitimately lands at zoom 4 with roughly 110° of longitude
     * on screen and ocean either side. An earlier version of this check
     * demanded the span be under twice Australia's width, which a correct fit
     * can never satisfy here — so it retried forever and never settled.
     *
     * Four times its width is the honest line: it accepts every correct fit and
     * still rejects the one-zoom-level-out failure this whole block exists to
     * catch, which puts 219° on screen.
     */
    const b = map.getBounds();
    const span = b.getEast() - b.getWest();
    fitted = b.contains(auBounds) && span <= auSpan * 4;
  }

  const onResize = () => layoutTick();
  const ro = new ResizeObserver(() => layoutTick());
  ro.observe(frame);
  addEventListener('resize', onResize);
  // A short retry ladder rather than a single deferred call: the container gets
  // its real size when main.ts moves this view's children into the page, and
  // Leaflet needs one more tick after that before `getSize()` reflects it. Each
  // attempt is a no-op once `fitted` is genuinely true.
  for (const delay of [0, 60, 200, 500, 1200]) setTimeout(layoutTick, delay);

  // main.ts gives views no teardown hook, so the map tears itself down once its
  // container has left the document. Without this, navigating away and back
  // leaves a live Leaflet instance and a live ResizeObserver behind each time.
  function teardownIfGone(): void {
    if (document.body.contains(frame)) return;
    removeEventListener('hashchange', teardownIfGone);
    removeEventListener('resize', onResize);
    ro.disconnect();
    map.remove();
  }
  addEventListener('hashchange', teardownIfGone);
}
